import { map } from "lodash";
import { Select } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";

import SelectComponents from "src/components/SelectComponents";
import usePagedTagSearch from "src/hooks/usePagedTagSearch";

import { SelectorContext } from "../TagsEditor/contexts/selectorContext";
import { MenuList } from "./components/MenuList";
import useSelectedTagValue from "./hooks/useSelectedTagValue";

const DEFAULT_PLACEHOLDER = "Type to search tag name or type";

const TagSearchSelect = ({
  tagTypes,
  value,
  onSelectTag,
  label,
  ariaLabel,
  disabled,
  invalid,
  mandatory,
  focus,
  placeholder = DEFAULT_PLACEHOLDER,
}) => {
  const { selectedValue, setSelectedValue, selectedValueLoading } =
    useSelectedTagValue(value);

  // Add state to control the input value
  const [inputValue, setInputValue] = useState("");

  const { tags, loading, loadOptions, loadMore, loadMoreLoading } =
    usePagedTagSearch({
      tagTypes,
    });

  const handleSelect = (selected) => {
    setSelectedValue(selected);
    onSelectTag(selected);
    // Clear the input value when an option is selected
    setInputValue("");
  };

  const options = map(tags, (tag) => ({
    label: tag.displayValue,
    value: tag.id,
  }));

  const handleInputChange = useCallback(
    (val, action) => {
      // Always update the input value
      setInputValue(val);

      // Only load options on actual input changes, not on blur or menu close
      if (action.action !== "input-blur" && action.action !== "menu-close") {
        loadOptions(val);
      }
    },
    [loadOptions]
  );

  // Load all options when the menu opens
  const handleMenuOpen = useCallback(() => {
    // Clear any stale input value when menu opens
    setInputValue("");
    // Always reload with empty search to show all options
    loadOptions("");
  }, [loadOptions]);

  return (
    <SelectorContext.Provider
      value={{
        loadMoreLoading,
      }}
    >
      <Select
        inputId="tagSearchSelector"
        label={label}
        ariaLabel={ariaLabel || label || "Search tags"}
        isDisabled={disabled}
        invalid={invalid}
        placeholder={placeholder}
        noOptionsMessage={() => "No search results"}
        cacheOptions={false}
        backspaceRemovesValue={true}
        isClearable={true}
        isSearchable
        isLoading={loading || selectedValueLoading}
        options={loading && options.length === 0 ? [] : options}
        onChange={handleSelect}
        onInputChange={handleInputChange}
        onMenuOpen={handleMenuOpen}
        value={selectedValue}
        inputValue={inputValue}
        menuPlacement="auto"
        components={{
          ...SelectComponents,
          MenuList,
        }}
        isValidNewOption={() => {
          return false;
        }}
        selectType="creatable"
        focus={focus}
        mandatory={mandatory}
        onMenuScrollToBottom={loadMore}
      />
    </SelectorContext.Provider>
  );
};

TagSearchSelect.propTypes = {
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  value: PropTypes.string,
  onSelectTag: PropTypes.func,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  mandatory: PropTypes.bool,
  focus: PropTypes.bool,
  tagTypes: PropTypes.array,
  placeholder: PropTypes.string,
};

export default TagSearchSelect;
